import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable, Subject, of, from } from 'rxjs'
import { catchError, map, mergeMap, takeUntil, toArray } from 'rxjs/operators'
import { AndroidbuildService } from '../android-build/androidbuild.service'
import { BuilderService } from 'src/app/builder-services/builder.service'
import { SettingsApiService } from 'src/app/builder-services/api/settings.api.service'
import { AutomationOptions, AutomationProgress, BuildResult, BuildSummary } from './automatic-build.types'

@Injectable({
  providedIn: 'root'
})
export class AndroidBuildAutomationService {
  // Cancellation support
  private cancelSubject = new Subject<void>()
  private isCancelled = false

  private isRunningSubject = new BehaviorSubject<boolean>(false)
  readonly isRunning$ = this.isRunningSubject.asObservable()

  // Current automation options
  private currentOptions: AutomationOptions = { buildType: 'aab', whatsNew: '', autoUpload: true }

  // Polling interval in milliseconds
  private readonly POLL_INTERVAL = 8000

  private progressSubject = new BehaviorSubject<AutomationProgress>({
    currentIndex: 0,
    totalProjects: 0,
    currentProject: '',
    currentPackageName: '',
    currentBuildProgress: 0,
    currentBuildStatus: '',
    results: []
  })
  readonly progress$ = this.progressSubject.asObservable()

  get isRunning(): boolean {
    return this.isRunningSubject.getValue()
  }

  get progress(): AutomationProgress {
    return this.progressSubject.getValue()
  }

  constructor(private builderService: BuilderService, private settingsApi: SettingsApiService, private androidBuildService: AndroidbuildService) {}

  /**
   * Cancels the ongoing automation process
   */
  cancel = (): void => {
    this.isCancelled = true
    this.cancelSubject.next()
  }

  /**
   * Resets the automation state for a new run
   */
  private reset = (): void => {
    this.isCancelled = false
    this.cancelSubject = new Subject<void>()
    this.progressSubject.next({
      currentIndex: 0,
      totalProjects: 0,
      currentProject: '',
      currentPackageName: '',
      currentBuildProgress: 0,
      currentBuildStatus: '',
      results: []
    })
  }

  /**
   * Main automation method: Fetches purchase projects from backend,
   * retrieves settings from Supabase, increments versions, and triggers builds for each valid project.
   * @param email User email to fetch purchase projects
   * @param options Automation options including buildType, whatsNew, and autoUpload flag
   * @returns Observable with build summary
   */
  runAutomatedBuilds = (email: string, options: AutomationOptions): Observable<BuildSummary> => {
    this.reset()
    this.isRunningSubject.next(true)
    this.currentOptions = options

    return this.builderService.getPurchaseProjects(email).pipe(
      takeUntil(this.cancelSubject),
      mergeMap((projectsObj: any) => {
        // Convert object response to array - getPurchaseProjects returns { "7903": { id: "7903", ... }, ... }
        const projects = projectsObj ? Object.values(projectsObj) : []

        if (!projects || projects.length === 0) {
          this.isRunningSubject.next(false)
          return of({ total: 0, completed: [], skipped: [], cancelled: [], failed: [] } as BuildSummary)
        }

        this.progressSubject.next({ ...this.progressSubject.getValue(), totalProjects: projects.length })

        // Process each project sequentially to avoid overwhelming the build system
        return from(projects).pipe(
          takeUntil(this.cancelSubject),
          mergeMap((project: any, index) => {
            if (this.isCancelled) {
              return of({
                projectId: project.id || 'unknown',
                packageName: 'unknown',
                status: 'cancelled',
                reason: 'Automation cancelled by user'
              } as BuildResult)
            }
            const current = this.progressSubject.getValue()
            this.progressSubject.next({
              ...current,
              currentIndex: index + 1,
              currentProject: project.id || 'unknown',
              currentBuildProgress: 0,
              currentBuildStatus: ''
            })
            return this.processProject(project)
          }, 1), // concurrency of 1
          toArray(),
          map((results: BuildResult[]) => {
            this.isRunningSubject.next(false)
            return this.generateSummary(results)
          })
        )
      }),
      catchError((error) => {
        console.error('Failed to fetch projects:', error)
        this.isRunningSubject.next(false)
        return of({ total: 0, completed: [], skipped: [], cancelled: [], failed: [] } as BuildSummary)
      })
    )
  }

  /**
   * Processes a single project: fetches settings, gets versions, increments, and triggers build
   */
  private processProject = (project: any): Observable<BuildResult> => {
    const projectId = project.id

    if (this.isCancelled) {
      const result: BuildResult = {
        projectId: projectId || 'unknown',
        packageName: 'unknown',
        status: 'cancelled',
        reason: 'Automation cancelled by user'
      }
      const current = this.progressSubject.getValue()
      this.progressSubject.next({ ...current, results: [...current.results, result] })
      return of(result)
    }

    if (!projectId) {
      const result: BuildResult = {
        projectId: 'unknown',
        packageName: 'unknown',
        status: 'skipped',
        reason: 'Project has no ID'
      }
      const current = this.progressSubject.getValue()
      this.progressSubject.next({ ...current, results: [...current.results, result] })
      return of(result)
    }

    // Step 1: Get general settings from Supabase to extract packageName
    return from(this.settingsApi.getSettingsByType(projectId, 'general_settings')).pipe(
      takeUntil(this.cancelSubject),
      mergeMap((settings: any) => {
        if (this.isCancelled) {
          const result: BuildResult = {
            projectId,
            packageName: 'unknown',
            status: 'cancelled',
            reason: 'Automation cancelled by user'
          }
          const current = this.progressSubject.getValue()
          this.progressSubject.next({ ...current, results: [...current.results, result] })
          return of(result)
        }

        const packageName = settings?.package_name
        this.progressSubject.next({ ...this.progressSubject.getValue(), currentPackageName: packageName || 'unknown' })

        if (!packageName) {
          const result: BuildResult = {
            projectId,
            packageName: 'unknown',
            status: 'skipped',
            reason: 'No package_name in general_settings'
          }
          const current = this.progressSubject.getValue()
          this.progressSubject.next({ ...current, results: [...current.results, result] })
          return of(result)
        }

        // Skip default package names
        // if (packageName === 'com.stacks.www' || packageName === 'com.stacksappsamples.www') {
        if (packageName === 'com.stacks.www') {
          const result: BuildResult = {
            projectId,
            packageName,
            status: 'skipped',
            reason: `Default package name (${packageName}) - not configured`
          }
          const current = this.progressSubject.getValue()
          this.progressSubject.next({ ...current, results: [...current.results, result] })
          return of(result)
        }

        // Step 2: Check if app is published on Google Play
        return this.androidBuildService.checkAppPublished(packageName).pipe(
          takeUntil(this.cancelSubject),
          mergeMap((publishedResponse) => {
            if (this.isCancelled) {
              const result: BuildResult = {
                projectId,
                packageName,
                status: 'cancelled',
                reason: 'Automation cancelled by user'
              }
              const current = this.progressSubject.getValue()
              this.progressSubject.next({ ...current, results: [...current.results, result] })
              return of(result)
            }

            // Check if app is published
            if (!publishedResponse.isPublished) {
              const result: BuildResult = {
                projectId,
                packageName,
                status: 'skipped',
                reason: publishedResponse.error || `App is not published on Google Play (status: ${publishedResponse.productionStatus || 'unknown'})`
              }
              const current = this.progressSubject.getValue()
              this.progressSubject.next({ ...current, results: [...current.results, result] })
              return of(result)
            }

            // Step 3: Get app versions from Google Play
            return this.androidBuildService.getAppVersions(packageName).pipe(
              takeUntil(this.cancelSubject),
              mergeMap((response: any) => {
                if (this.isCancelled) {
                  const result: BuildResult = {
                    projectId,
                    packageName,
                    status: 'cancelled',
                    reason: 'Automation cancelled by user'
                  }
                  const current = this.progressSubject.getValue()
                  this.progressSubject.next({ ...current, results: [...current.results, result] })
                  return of(result)
                }

                const versions = response?.data || []
                const productionVersion = versions.find((v: any) => v.track === 'production')

                // Check if production release exists
                if (!productionVersion?.parsedVersion) {
                  const result: BuildResult = {
                    projectId,
                    packageName,
                    status: 'skipped',
                    reason: 'No production version found on Google Play'
                  }
                  const current = this.progressSubject.getValue()
                  this.progressSubject.next({ ...current, results: [...current.results, result] })
                  return of(result)
                }

                const currentVersionName = productionVersion.parsedVersion.name
                const currentVersionCode = productionVersion.parsedVersion.code

                // Step 4: Increment version
                const newVersionName = this.incrementVersion(currentVersionName)
                const newVersionCode = (parseInt(currentVersionCode, 10) || 0) + 1

                // Step 5: Trigger build and poll for progress
                return this.startBuildAndPollProgress(projectId, packageName, newVersionName, newVersionCode, this.currentOptions.buildType, currentVersionName, currentVersionCode)
              }),
              catchError((error) => {
                const result: BuildResult = {
                  projectId,
                  packageName,
                  status: 'skipped',
                  reason: `Failed to get app versions: ${error.message || 'Unknown error'}`
                }
                const current = this.progressSubject.getValue()
                this.progressSubject.next({ ...current, results: [...current.results, result] })
                return of(result)
              })
            )
          }),
          catchError((error) => {
            const result: BuildResult = {
              projectId,
              packageName,
              status: 'skipped',
              reason: `Failed to check app publish status: ${error.message || 'Unknown error'}`
            }
            const current = this.progressSubject.getValue()
            this.progressSubject.next({ ...current, results: [...current.results, result] })
            return of(result)
          })
        )
      }),
      catchError((error) => {
        const result: BuildResult = {
          projectId,
          packageName: 'unknown',
          status: 'skipped',
          reason: `Failed to get settings: ${error.message || 'Unknown error'}`
        }
        const current = this.progressSubject.getValue()
        this.progressSubject.next({ ...current, results: [...current.results, result] })
        return of(result)
      })
    )
  }

  /**
   * Starts a build and polls for progress until completion or failure
   */
  private startBuildAndPollProgress = (projectId: string, packageName: string, newVersionName: number, newVersionCode: number, buildType: string, oldVersionName: string, oldVersionCode: number): Observable<BuildResult> => {
    this.progressSubject.next({ ...this.progressSubject.getValue(), currentBuildStatus: 'Starting build...', currentBuildProgress: 0 })

    return this.androidBuildService.startBuild(projectId, newVersionName, newVersionCode, buildType).pipe(
      takeUntil(this.cancelSubject),
      mergeMap(() => {
        // Build started successfully, now poll for progress
        return this.pollBuildProgress(projectId, newVersionName).pipe(
          mergeMap((pollResult) => {
            // Build failed - return result without upload attempt
            if (!pollResult.success) {
              const result: BuildResult = {
                projectId,
                packageName,
                status: 'failed',
                reason: pollResult.error,
                oldVersion: oldVersionName,
                newVersion: String(newVersionName),
                oldVersionCode: oldVersionCode,
                newVersionCode: newVersionCode,
                buildProgress: pollResult.progress,
                apkUrl: pollResult.apkUrl,
                aabUrl: pollResult.aabUrl
              }
              const current = this.progressSubject.getValue()
              this.progressSubject.next({ ...current, results: [...current.results, result] })
              return of(result)
            }

            // Build succeeded - check if auto-upload is enabled
            if (!this.currentOptions.autoUpload) {
              const result: BuildResult = {
                projectId,
                packageName,
                status: 'completed',
                oldVersion: oldVersionName,
                newVersion: String(newVersionName),
                oldVersionCode: oldVersionCode,
                newVersionCode: newVersionCode,
                buildProgress: 100,
                apkUrl: pollResult.apkUrl,
                aabUrl: pollResult.aabUrl,
                uploadStatus: 'skipped'
              }
              const current = this.progressSubject.getValue()
              this.progressSubject.next({ ...current, results: [...current.results, result] })
              return of(result)
            }

            // Proceed with upload to Google Play
            this.progressSubject.next({ ...this.progressSubject.getValue(), currentBuildStatus: 'Uploading to Google Play...' })
            const whatsNew = this.currentOptions.whatsNew || 'Bug fixes and performance improvements'

            return this.androidBuildService.uploadToGooglePlay(packageName, projectId, newVersionCode, String(newVersionName), whatsNew).pipe(
              map((uploadResponse: any) => {
                const result: BuildResult = {
                  projectId,
                  packageName,
                  status: 'completed',
                  oldVersion: oldVersionName,
                  newVersion: String(newVersionName),
                  oldVersionCode: oldVersionCode,
                  newVersionCode: newVersionCode,
                  buildProgress: 100,
                  apkUrl: pollResult.apkUrl,
                  aabUrl: pollResult.aabUrl,
                  uploadStatus: 'success'
                }
                const current = this.progressSubject.getValue()
                this.progressSubject.next({ ...current, results: [...current.results, result] })
                return result
              }),
              catchError((uploadError) => {
                const result: BuildResult = {
                  projectId,
                  packageName,
                  status: 'completed', // Build succeeded even if upload failed
                  oldVersion: oldVersionName,
                  newVersion: String(newVersionName),
                  oldVersionCode: oldVersionCode,
                  newVersionCode: newVersionCode,
                  buildProgress: 100,
                  apkUrl: pollResult.apkUrl,
                  aabUrl: pollResult.aabUrl,
                  uploadStatus: 'failed',
                  uploadError: uploadError?.error?.message || uploadError?.message || 'Upload to Google Play failed'
                }
                const current = this.progressSubject.getValue()
                this.progressSubject.next({ ...current, results: [...current.results, result] })
                return of(result)
              })
            )
          })
        )
      }),
      catchError((error) => {
        const result: BuildResult = {
          projectId,
          packageName,
          status: 'failed',
          reason: `Build start failed: ${error.message || 'Unknown error'}`,
          oldVersion: oldVersionName,
          newVersion: String(newVersionName),
          oldVersionCode: oldVersionCode,
          newVersionCode: newVersionCode
        }
        const current = this.progressSubject.getValue()
        this.progressSubject.next({ ...current, results: [...current.results, result] })
        return of(result)
      })
    )
  }

  /**
   * Polls the build progress until completion, failure, or cancellation
   * Matches the approach used in BuildStatusComponent.getAndroidStatus
   */
  private pollBuildProgress = (projectId: string, version: number): Observable<{ success: boolean; progress: number; error?: string; apkUrl?: string; aabUrl?: string }> => {
    return new Observable((observer) => {
      const poll = () => {
        if (this.isCancelled) {
          observer.next({ success: false, progress: 0, error: 'Cancelled by user' })
          observer.complete()
          return
        }

        this.androidBuildService.getProgress(projectId, version).subscribe(
          (res: any) => {
            const progress = Number(res?.data) || 0
            const status = res?.status || ''
            const apkUrl = res?.android_url
            const aabUrl = res?.aab_url

            this.progressSubject.next({ ...this.progressSubject.getValue(), currentBuildProgress: progress, currentBuildStatus: status })

            // Check for completion or error
            if (status === 'error' || progress >= 100 || status === 'ready' || status === 'complete') {
              if (status === 'error') {
                // Try to get error details
                this.androidBuildService.getBuildError(projectId).subscribe({
                  next: (errorRes: any) => {
                    const errorMsg = errorRes?.data?.error || errorRes?.message || 'Build failed'
                    observer.next({ success: false, progress, error: errorMsg })
                    observer.complete()
                  },
                  error: () => {
                    observer.next({ success: false, progress, error: 'Build failed with unknown error' })
                    observer.complete()
                  }
                })
              } else {
                observer.next({ success: true, progress: 100, apkUrl, aabUrl })
                observer.complete()
              }
            } else {
              // Continue polling
              setTimeout(poll, this.POLL_INTERVAL)
            }
          },
          // On progress error, complete and move to next (same as getAndroidStatus)
          () => {
            observer.next({ success: false, progress: 0, error: 'Failed to get build progress' })
            observer.complete()
          }
        )
      }

      // Start polling
      poll()
    })
  }

  /**
   * Increments a version by 0.1
   * Returns a NUMBER to match BuildStatusComponent behavior (required for proper JSON serialization)
   */
  incrementVersion = (version: string): number => {
    if (!version) {
      return 1.0
    }

    const versionStr = String(version).trim()
    const currentVersion = parseFloat(versionStr)

    if (!isNaN(currentVersion)) {
      return parseFloat((currentVersion + 0.1).toFixed(1))
    }

    // Default fallback
    return 1.0
  }

  /**
   * Generates a summary from the build results
   */
  private generateSummary = (results: BuildResult[]): BuildSummary => {
    const completed = results.filter((r) => r.status === 'completed')
    const skipped = results.filter((r) => r.status === 'skipped')
    const cancelled = results.filter((r) => r.status === 'cancelled')
    const failed = results.filter((r) => r.status === 'failed')

    return {
      total: results.length,
      completed,
      skipped,
      cancelled,
      failed
    }
  }

  /**
   * Prints a formatted summary to the console
   */
  printSummary = (summary: BuildSummary): void => {
    console.log('\n========== ANDROID BUILD AUTOMATION SUMMARY ==========')
    console.log(`Total Projects: ${summary.total}`)
    console.log(`Completed Builds: ${summary.completed.length}`)
    console.log(`Failed Builds: ${summary.failed.length}`)
    console.log(`Skipped Projects: ${summary.skipped.length}`)
    console.log(`Cancelled: ${summary.cancelled.length}`)

    if (summary.completed.length > 0) {
      console.log('\n--- COMPLETED BUILDS ---')
      summary.completed.forEach((build) => {
        console.log(`  ✓ ${build.packageName} (ID: ${build.projectId})`)
        console.log(`    Version: ${build.oldVersion} → ${build.newVersion}`)
        console.log(`    Version Code: ${build.oldVersionCode} → ${build.newVersionCode}`)
        if (build.aabUrl) console.log(`    AAB: ${build.aabUrl}`)
        if (build.apkUrl) console.log(`    APK: ${build.apkUrl}`)
      })
    }

    if (summary.failed.length > 0) {
      console.log('\n--- FAILED BUILDS ---')
      summary.failed.forEach((build) => {
        console.log(`  ✗ ${build.packageName} (ID: ${build.projectId})`)
        console.log(`    Reason: ${build.reason}`)
      })
    }

    if (summary.skipped.length > 0) {
      console.log('\n--- SKIPPED PROJECTS ---')
      summary.skipped.forEach((build) => {
        console.log(`  ○ ${build.packageName || build.projectId}`)
        console.log(`    Reason: ${build.reason}`)
      })
    }

    if (summary.cancelled.length > 0) {
      console.log('\n--- CANCELLED ---')
      summary.cancelled.forEach((build) => {
        console.log(`  ⊘ ${build.packageName || build.projectId}`)
      })
    }

    console.log('\n========================================================\n')
  }

  /**
   * Convenience method to run automation and print summary
   */
  runAndPrintSummary = (email: string, options?: AutomationOptions): Observable<BuildSummary> => {
    const opts = options || { buildType: 'aab', whatsNew: '', autoUpload: true }
    return this.runAutomatedBuilds(email, opts).pipe(
      map((summary) => {
        this.printSummary(summary)
        return summary
      })
    )
  }
}
